import { supabase } from '../supabaseClient'
import { nombreSeguroStorage } from './nombre-storage'
import {
  calcularIPER,
  NivelDeficiencia,
  NivelExposicion,
  NivelConsecuencia,
} from './iperCalculations'
import {
  ChemicalProduct,
  CoshhCantidad,
  CoshhVolatilidad,
  calcularNivelCoshh,
} from './coshhEssentials'
import { BiologicalAgent, BiologicalExposureInput, HygieneAnswers, HygieneResponse } from './biogaval'
import { hoyEc } from './fechas'

export interface Process {
  id: string
  company_id: string
  name: string
  description: string | null
  sort_order: number
  created_at: string
  updated_at: string
}

export interface ProcessActivity {
  id: string
  company_id: string
  process_id: string
  position_id: string | null
  name: string
  sort_order: number
  created_at: string
  updated_at: string
}

export interface ActivityTask {
  id: string
  company_id: string
  activity_id: string
  name: string
  duration_minutes: number | null
  sort_order: number
  created_at: string
  updated_at: string
}

export interface ActivityWithTasks extends ProcessActivity {
  activity_tasks: ActivityTask[]
}

export interface ProcessWithHierarchy extends Process {
  process_activities: ActivityWithTasks[]
}

// ============================================================
// LECTURA — jerarquía completa en una sola query anidada
// ============================================================

export async function fetchProcessesHierarchy(companyId: string): Promise<ProcessWithHierarchy[]> {
  if (!companyId) return []

  const { data, error } = await supabase
    .from('processes')
    .select('*, process_activities(*, activity_tasks(*))')
    .eq('company_id', companyId)
    .order('sort_order', { ascending: true })
    .order('sort_order', { ascending: true, foreignTable: 'process_activities' })
    .order('sort_order', { ascending: true, foreignTable: 'process_activities.activity_tasks' })

  if (error) throw error
  return (data ?? []) as unknown as ProcessWithHierarchy[]
}

// ============================================================
// PROCESSES
// ============================================================

export async function createProcess(companyId: string, name: string, description?: string | null): Promise<Process> {
  const { count } = await supabase
    .from('processes')
    .select('id', { count: 'exact', head: true })
    .eq('company_id', companyId)

  const { data, error } = await supabase
    .from('processes')
    .insert({ company_id: companyId, name, description: description ?? null, sort_order: count ?? 0 })
    .select()
    .single()

  if (error) throw error
  return data as Process
}

export async function updateProcess(id: string, patch: Partial<Pick<Process, 'name' | 'description'>>): Promise<void> {
  const { error } = await supabase.from('processes').update(patch).eq('id', id)
  if (error) throw error
}

export async function deleteProcess(id: string): Promise<void> {
  const { error } = await supabase.from('processes').delete().eq('id', id)
  if (error) throw error
}

export async function reorderProcesses(orderedIds: string[]): Promise<void> {
  await Promise.all(orderedIds.map((id, index) => supabase.from('processes').update({ sort_order: index }).eq('id', id)))
}

// ============================================================
// PROCESS_ACTIVITIES
// ============================================================

export async function createActivity(companyId: string, processId: string, name: string, positionId?: string | null): Promise<ProcessActivity> {
  const { count } = await supabase
    .from('process_activities')
    .select('id', { count: 'exact', head: true })
    .eq('process_id', processId)

  const { data, error } = await supabase
    .from('process_activities')
    .insert({ company_id: companyId, process_id: processId, position_id: positionId ?? null, name, sort_order: count ?? 0 })
    .select()
    .single()

  if (error) throw error
  return data as ProcessActivity
}

export async function updateActivity(id: string, patch: Partial<Pick<ProcessActivity, 'name' | 'position_id'>>): Promise<void> {
  const { error } = await supabase.from('process_activities').update(patch).eq('id', id)
  if (error) throw error
}

export async function deleteActivity(id: string): Promise<void> {
  const { error } = await supabase.from('process_activities').delete().eq('id', id)
  if (error) throw error
}

export async function reorderActivities(orderedIds: string[]): Promise<void> {
  await Promise.all(orderedIds.map((id, index) => supabase.from('process_activities').update({ sort_order: index }).eq('id', id)))
}

// ============================================================
// ACTIVITY_TASKS
// ============================================================

export async function createTask(companyId: string, activityId: string, name: string, durationMinutes?: number | null): Promise<ActivityTask> {
  const { count } = await supabase
    .from('activity_tasks')
    .select('id', { count: 'exact', head: true })
    .eq('activity_id', activityId)

  const { data, error } = await supabase
    .from('activity_tasks')
    .insert({ company_id: companyId, activity_id: activityId, name, duration_minutes: durationMinutes ?? null, sort_order: count ?? 0 })
    .select()
    .single()

  if (error) throw error
  return data as ActivityTask
}

export async function updateTask(id: string, patch: Partial<Pick<ActivityTask, 'name' | 'duration_minutes'>>): Promise<void> {
  const { error } = await supabase.from('activity_tasks').update(patch).eq('id', id)
  if (error) throw error
}

export async function deleteTask(id: string): Promise<void> {
  const { error } = await supabase.from('activity_tasks').delete().eq('id', id)
  if (error) throw error
}

export async function reorderTasks(orderedIds: string[]): Promise<void> {
  await Promise.all(orderedIds.map((id, index) => supabase.from('activity_tasks').update({ sort_order: index }).eq('id', id)))
}

// ============================================================
// JOB_POSITIONS — campos legales (GT-02) editables desde el
// detalle de actividad
// ============================================================

export interface JobPositionLegalFields {
  id: string
  name: string
  numero_trabajadores: number | null
  actividades_realizadas: string | null
  horas_actividad_diarias: number | null
  recursos_utilizados: string | null
  materiales_agentes: string | null
}

export async function fetchJobPositionsFull(companyId: string): Promise<JobPositionLegalFields[]> {
  if (!companyId) return []
  const { data, error } = await supabase
    .from('job_positions')
    .select('id, name, numero_trabajadores, actividades_realizadas, horas_actividad_diarias, recursos_utilizados, materiales_agentes')
    .eq('company_id', companyId)
    .order('name')

  if (error) throw error
  return (data ?? []) as JobPositionLegalFields[]
}

export async function updateJobPositionLegalFields(
  id: string,
  patch: Partial<Pick<JobPositionLegalFields, 'numero_trabajadores' | 'actividades_realizadas' | 'horas_actividad_diarias' | 'recursos_utilizados' | 'materiales_agentes'>>
): Promise<void> {
  const { error } = await supabase.from('job_positions').update(patch).eq('id', id)
  if (error) throw error
}

// ============================================================
// TASK HAZARDS — identificación de peligros y evaluación GTC-45
// por tarea (GT-04), vinculada a task_id
// ============================================================

export interface TaskHazardAssessment {
  id: string
  task_id: string
  hazard_category: string
  hazard_type: string
  hazard_description: string | null
  possible_effects: string | null
  control_source: string | null
  control_medium: string | null
  control_individual: string | null
  nivel_deficiencia: number
  nivel_exposicion: number
  nivel_probabilidad: number
  interpretacion_probabilidad: string
  nivel_consecuencia: number
  nivel_riesgo: number
  interpretacion_riesgo: string
  aceptabilidad: string
  medida_eliminacion: string | null
  medida_sustitucion: string | null
  medida_control_ingenieria: string | null
  medida_control_administrativo: string | null
  medida_epp: string | null
}

export interface HazardCatalogItem {
  id: string
  category: string
  hazard_type: string
  display_order: number
}

export async function fetchHazardCatalog(): Promise<HazardCatalogItem[]> {
  const { data, error } = await supabase
    .from('iper_hazard_catalog')
    .select('id, category, hazard_type, display_order')
    .order('category')
    .order('display_order')

  if (error) throw error
  return (data ?? []) as HazardCatalogItem[]
}

export async function fetchTaskHazards(taskId: string): Promise<TaskHazardAssessment[]> {
  if (!taskId) return []
  const { data, error } = await supabase
    .from('iper_assessments')
    .select('id, task_id, hazard_category, hazard_type, hazard_description, possible_effects, control_source, control_medium, control_individual, nivel_deficiencia, nivel_exposicion, nivel_probabilidad, interpretacion_probabilidad, nivel_consecuencia, nivel_riesgo, interpretacion_riesgo, aceptabilidad, medida_eliminacion, medida_sustitucion, medida_control_ingenieria, medida_control_administrativo, medida_epp')
    .eq('task_id', taskId)
    .order('created_at')

  if (error) throw error
  return (data ?? []) as TaskHazardAssessment[]
}

export interface CreateTaskHazardInput {
  company_id: string
  task_id: string
  position_id: string | null
  process_name: string
  activity: string
  task: string
  hazard_category: string
  hazard_type: string
  hazard_description: string | null
  possible_effects: string | null
  control_source: string | null
  control_medium: string | null
  control_individual: string | null
  nivel_deficiencia: NivelDeficiencia
  nivel_exposicion: NivelExposicion
  nivel_consecuencia: NivelConsecuencia
  medida_eliminacion: string | null
  medida_sustitucion: string | null
  medida_control_ingenieria: string | null
  medida_control_administrativo: string | null
  medida_epp: string | null
}

export async function createTaskHazard(input: CreateTaskHazardInput): Promise<void> {
  const calc = calcularIPER(input.nivel_deficiencia, input.nivel_exposicion, input.nivel_consecuencia)

  const { error } = await supabase.from('iper_assessments').insert({
    company_id: input.company_id,
    task_id: input.task_id,
    position_id: input.position_id,
    process_name: input.process_name,
    zone_location: input.activity,
    activity: input.activity,
    task: input.task,
    is_routine: true,
    hazard_category: input.hazard_category,
    hazard_type: input.hazard_type,
    hazard_description: input.hazard_description,
    possible_effects: input.possible_effects,
    control_source: input.control_source,
    control_medium: input.control_medium,
    control_individual: input.control_individual,
    nivel_deficiencia: input.nivel_deficiencia,
    nivel_exposicion: input.nivel_exposicion,
    nivel_probabilidad: calc.nivel_probabilidad,
    interpretacion_probabilidad: calc.interpretacion_probabilidad,
    nivel_consecuencia: input.nivel_consecuencia,
    nivel_riesgo: calc.nivel_riesgo,
    interpretacion_riesgo: calc.interpretacion_riesgo,
    aceptabilidad: calc.aceptabilidad,
    medida_eliminacion: input.medida_eliminacion,
    medida_sustitucion: input.medida_sustitucion,
    medida_control_ingenieria: input.medida_control_ingenieria,
    medida_control_administrativo: input.medida_control_administrativo,
    medida_epp: input.medida_epp,
    fecha_evaluacion: hoyEc(),
  })

  if (error) throw error
}

export async function deleteTaskHazard(id: string): Promise<void> {
  const { error } = await supabase.from('iper_assessments').delete().eq('id', id)
  if (error) throw error
}

export interface HygieneDocument {
  id: string
  task_id: string
  category: 'fisico' | 'quimico' | 'biologico'
  title: string
  file_url: string
  file_name: string
  version: number
  is_current: boolean
  created_at: string
}

const HYGIENE_CHECKLIST_ITEM: Record<'fisico' | 'quimico' | 'biologico', string> = {
  fisico: 'GT-15',
  quimico: 'GT-16',
  biologico: 'GT-17',
}

const HYGIENE_CATEGORY_BY_ITEM: Record<string, 'fisico' | 'quimico' | 'biologico'> = {
  'GT-15': 'fisico',
  'GT-16': 'quimico',
  'GT-17': 'biologico',
}

export async function fetchHygieneDocuments(taskId: string): Promise<HygieneDocument[]> {
  if (!taskId) return []
  const { data, error } = await supabase
    .from('sso_documents')
    .select('id, task_id, checklist_item_id, title, file_url, file_name, version, is_current, created_at')
    .eq('task_id', taskId)
    .order('checklist_item_id')
    .order('version', { ascending: false })

  if (error) throw error
  return (data ?? []).map((d: any) => ({
    id: d.id,
    task_id: d.task_id,
    category: HYGIENE_CATEGORY_BY_ITEM[d.checklist_item_id] ?? 'fisico',
    title: d.title,
    file_url: d.file_url,
    file_name: d.file_name,
    version: d.version,
    is_current: d.is_current,
    created_at: d.created_at,
  })) as HygieneDocument[]
}

export async function uploadHygieneDocument(
  companyId: string,
  taskId: string,
  category: 'fisico' | 'quimico' | 'biologico',
  title: string,
  file: File,
  uploadedBy: string | null
): Promise<void> {
  const { data: existingRows } = await supabase
    .from('sso_documents')
    .select('id, version')
    .eq('company_id', companyId)
    .eq('task_id', taskId)
    .eq('checklist_item_id', HYGIENE_CHECKLIST_ITEM[category])
    .eq('is_current', true)
    .order('version', { ascending: false })
    .limit(1)

  const existing = existingRows?.[0] ?? null
  const nextVersion = existing ? existing.version + 1 : 1

  const ext = file.name.split('.').pop() ?? 'pdf'
  const path = `${companyId}/gestion-tecnica/${nombreSeguroStorage(`${taskId}-${category}-v${nextVersion}.${ext}`)}`

  const { error: upErr } = await supabase.storage
    .from('sso-documents')
    .upload(path, file, { upsert: false })

  if (upErr) throw upErr

  const { data: pub } = supabase.storage.from('sso-documents').getPublicUrl(path)

  if (existing) {
    await supabase
      .from('sso_documents')
      .update({ is_current: false })
      .eq('id', existing.id)
  }

  const { error: insErr } = await supabase.from('sso_documents').insert({
    company_id: companyId,
    task_id: taskId,
    category: 'procedimiento',
    title: title.trim(),
    file_url: pub.publicUrl,
    file_name: file.name,
    uploaded_by: uploadedBy,
    version: nextVersion,
    parent_document_id: existing?.id ?? null,
    is_current: true,
    checklist_item_id: HYGIENE_CHECKLIST_ITEM[category],
  })

  if (insErr) throw insErr
}

export interface TaskChemicalAssessment {
  id: string
  task_id: string
  product_id: string | null
  product_name_snapshot: string
  cantidad: CoshhCantidad
  peligrosidad: string
  volatilidad: CoshhVolatilidad
  pulverulencia_manual: string | null
  nivel_riesgo: number
  accion_recomendada: string
  created_at: string
}

export async function fetchChemicalProducts(companyId: string): Promise<ChemicalProduct[]> {
  if (!companyId) return []
  const { data, error } = await supabase
    .from('products')
    .select('id, name, coshh_h_phrases, coshh_estado_fisico, coshh_punto_ebullicion')
    .eq('company_id', companyId)
    .eq('status', 'active')
    .order('name')

  if (error) throw error
  return (data ?? []) as ChemicalProduct[]
}

export async function fetchTaskChemicalAssessments(taskId: string): Promise<TaskChemicalAssessment[]> {
  if (!taskId) return []
  const { data, error } = await supabase
    .from('task_chemical_assessments')
    .select('id, task_id, product_id, product_name_snapshot, cantidad, peligrosidad, volatilidad, pulverulencia_manual, nivel_riesgo, accion_recomendada, created_at')
    .eq('task_id', taskId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return (data ?? []) as TaskChemicalAssessment[]
}

export interface CreateChemicalAssessmentInput {
  companyId: string
  taskId: string
  product: ChemicalProduct
  peligrosidad: string
  cantidad: CoshhCantidad
  volatilidad: CoshhVolatilidad
  pulverulenciaManual: string | null
  createdBy: string | null
}

export async function createTaskChemicalAssessment(input: CreateChemicalAssessmentInput): Promise<void> {
  const resultado = calcularNivelCoshh(input.peligrosidad as any, input.volatilidad, input.cantidad, input.product.coshh_estado_fisico)

  const { error } = await supabase.from('task_chemical_assessments').insert({
    company_id: input.companyId,
    task_id: input.taskId,
    product_id: input.product.id,
    product_name_snapshot: input.product.name,
    cantidad: input.cantidad,
    peligrosidad: input.peligrosidad,
    volatilidad: input.volatilidad,
    pulverulencia_manual: input.pulverulenciaManual,
    nivel_riesgo: resultado.nivel,
    accion_recomendada: resultado.accion,
    created_by: input.createdBy,
  })

  if (error) throw error
}

export async function deleteTaskChemicalAssessment(id: string): Promise<void> {
  const { error } = await supabase.from('task_chemical_assessments').delete().eq('id', id)
  if (error) throw error
}

export async function fetchCompanyChemicalAssessments(companyId: string): Promise<TaskChemicalAssessment[]> {
  if (!companyId) return []
  const { data, error } = await supabase
    .from('task_chemical_assessments')
    .select('id, task_id, product_id, product_name_snapshot, cantidad, peligrosidad, volatilidad, pulverulencia_manual, nivel_riesgo, accion_recomendada, created_at')
    .eq('company_id', companyId)
    .is('task_id', null)
    .order('created_at', { ascending: false })

  if (error) throw error
  return (data ?? []) as TaskChemicalAssessment[]
}

export interface CreateCompanyChemicalAssessmentInput {
  companyId: string
  product: ChemicalProduct
  peligrosidad: string
  cantidad: CoshhCantidad
  volatilidad: CoshhVolatilidad
  pulverulenciaManual: string | null
  createdBy: string | null
}

export async function createCompanyChemicalAssessment(input: CreateCompanyChemicalAssessmentInput): Promise<void> {
  const resultado = calcularNivelCoshh(input.peligrosidad as any, input.volatilidad, input.cantidad, input.product.coshh_estado_fisico)

  const { error } = await supabase.from('task_chemical_assessments').insert({
    company_id: input.companyId,
    task_id: null,
    product_id: input.product.id,
    product_name_snapshot: input.product.name,
    cantidad: input.cantidad,
    peligrosidad: input.peligrosidad,
    volatilidad: input.volatilidad,
    pulverulencia_manual: input.pulverulenciaManual,
    nivel_riesgo: resultado.nivel,
    accion_recomendada: resultado.accion,
    created_by: input.createdBy,
  })

  if (error) throw error
}

// ===== BIOGAVAL — Riesgo biológico por puesto de trabajo =====

export async function fetchBiologicalAgents(): Promise<BiologicalAgent[]> {
  const { data, error } = await supabase
    .from('biological_agents')
    .select('id, name, grupo, vias_transmision, sector_tipico, notes')
    .order('name')
  if (error) throw error
  return (data ?? []) as BiologicalAgent[]
}

export interface JobPositionBiologicalExposureRow {
  id: string
  agent_id: string
  agent_name: string
  agent_grupo: 1 | 2 | 3 | 4
  agent_vias: string[]
  via_probabilidad: 'prevalencia' | 'incidencia'
  valor_probabilidad: number
  frecuencia_pct: number
  vacunacion_pct: number
}

export async function fetchBiologicalExposures(jobPositionId: string): Promise<JobPositionBiologicalExposureRow[]> {
  const { data, error } = await supabase
    .from('job_position_biological_exposure')
    .select('id, agent_id, via_probabilidad, valor_probabilidad, frecuencia_pct, vacunacion_pct, biological_agents(name, grupo, vias_transmision)')
    .eq('job_position_id', jobPositionId)
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    id: r.id,
    agent_id: r.agent_id,
    agent_name: r.biological_agents?.name ?? '',
    agent_grupo: r.biological_agents?.grupo ?? 1,
    agent_vias: r.biological_agents?.vias_transmision ?? [],
    via_probabilidad: r.via_probabilidad,
    valor_probabilidad: r.valor_probabilidad,
    frecuencia_pct: r.frecuencia_pct,
    vacunacion_pct: r.vacunacion_pct,
  }))
}

export interface CreateBiologicalExposureInput {
  companyId: string
  jobPositionId: string
  exposure: BiologicalExposureInput
}

export async function createBiologicalExposure(input: CreateBiologicalExposureInput): Promise<void> {
  const { error } = await supabase.from('job_position_biological_exposure').insert({
    company_id: input.companyId,
    job_position_id: input.jobPositionId,
    agent_id: input.exposure.agent_id,
    via_probabilidad: input.exposure.via_probabilidad,
    valor_probabilidad: input.exposure.valor_probabilidad,
    frecuencia_pct: input.exposure.frecuencia_pct,
    vacunacion_pct: input.exposure.vacunacion_pct,
  })
  if (error) throw error
}

export async function deleteBiologicalExposure(id: string): Promise<void> {
  const { error } = await supabase.from('job_position_biological_exposure').delete().eq('id', id)
  if (error) throw error
}

export async function fetchBiologicalHygiene(jobPositionId: string): Promise<HygieneAnswers> {
  const { data, error } = await supabase
    .from('job_position_biological_hygiene')
    .select('item_id, respuesta')
    .eq('job_position_id', jobPositionId)
  if (error) throw error
  const answers: HygieneAnswers = {}
  for (const row of data ?? []) {
    answers[row.item_id] = row.respuesta as HygieneResponse
  }
  return answers
}

export async function saveBiologicalHygiene(companyId: string, jobPositionId: string, answers: HygieneAnswers): Promise<void> {
  const rows = Object.entries(answers).map(([item_id, respuesta]) => ({
    company_id: companyId,
    job_position_id: jobPositionId,
    item_id,
    respuesta,
  }))
  const { error } = await supabase
    .from('job_position_biological_hygiene')
    .upsert(rows, { onConflict: 'job_position_id,item_id' })
  if (error) throw error
}
